import Sidebar from "../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../context/AuthContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { getCuotaPorUnidad, insertCuota, updateCuota, CuotaAdmin } from "../../../api/cuotaAdminService";
import { Loader2, Plus, Pencil, Link as LinkIcon } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import Swal from "sweetalert2";

interface CuotaFormData {
  codigo: string;
  Nombre: string;
  valor: string;
  cau_dia_pago: string;
  Banco: string;
  link_Pago: string;
  Default: 0 | 1;
  Activo: 0 | 1;
}

const emptyForm: CuotaFormData = {
  codigo: "",
  Nombre: "",
  valor: "",
  cau_dia_pago: "",
  Banco: "",
  link_Pago: "",
  Default: 0,
  Activo: 1,
};

export default function AdminConfigPage() {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  const [cuotas, setCuotas] = useState<CuotaAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<CuotaFormData>(emptyForm);

  const fetchCuotas = useCallback(async () => {
    if (!unidadId) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await getCuotaPorUnidad({
        unidad_recidencial_codigo: unidadId,
        codigo: "",
        apartamento_codigo: "",
        activo: "",
      });
      setCuotas(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error al cargar cuotas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No fue posible cargar las cuotas de administración.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsLoading(false);
    }
  }, [unidadId]);

  useEffect(() => {
    fetchCuotas();
  }, [fetchCuotas]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = async (cuota: CuotaAdmin) => {
    try {
      const response = await getCuotaPorUnidad({
        unidad_recidencial_codigo: unidadId,
        codigo: cuota.cau_codigo.toString(),
        apartamento_codigo: "",
        activo: "",
      });
      const detail = Array.isArray(response) && response.length > 0 ? response[0] : cuota;

      setFormData({
        codigo: detail.cau_codigo.toString(),
        Nombre: detail.cau_nombre || "",
        valor: detail.cau_valor.toString(),
        cau_dia_pago: detail.cau_dia_pago.toString(),
        Banco: detail.cau_banco || "",
        link_Pago: detail.cau_link_pago || "",
        Default: detail.cau_default,
        Activo: detail.cau_activo,
      });
      setIsEditing(true);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error al cargar detalle de cuota:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No fue posible cargar el detalle de la cuota.",
        confirmButtonColor: "#d33",
      });
    }
  };

  const validateForm = () => {
    if (
      !formData.Nombre.trim() ||
      !formData.valor.trim() ||
      !formData.cau_dia_pago.trim() ||
      !formData.Banco.trim() ||
      !formData.link_Pago.trim()
    ) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Debes completar todos los campos obligatorios.",
        confirmButtonColor: "#3085d6",
      });
      return false;
    }

    if (Number.isNaN(Number(formData.valor)) || Number(formData.valor) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Valor inválido",
        text: "El valor de la cuota debe ser numérico y mayor a 0.",
        confirmButtonColor: "#3085d6",
      });
      return false;
    }

    const diaPago = Number(formData.cau_dia_pago);
    if (Number.isNaN(diaPago) || diaPago < 1 || diaPago > 31) {
      Swal.fire({
        icon: "warning",
        title: "Día inválido",
        text: "El día de pago debe estar entre 1 y 31.",
        confirmButtonColor: "#3085d6",
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!unidadId) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        const payload = {
          codigo: formData.codigo,
          unidad_recidencial_codigo: unidadId,
          Nombre: formData.Nombre.trim(),
          valor: Number(formData.valor),
          link_Pago: formData.link_Pago.trim(),
          Banco: formData.Banco.trim(),
          cau_dia_pago: Number(formData.cau_dia_pago),
          Default: formData.Default,
          Activo: formData.Activo,
        };
        await updateCuota(payload);
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Cuota actualizada exitosamente.",
          confirmButtonColor: "#3085d6",
        });
      } else {
        const payload = {
          unidad_recidencial_codigo: unidadId,
          Nombre: formData.Nombre.trim(),
          valor: Number(formData.valor),
          link_Pago: formData.link_Pago.trim(),
          Banco: formData.Banco.trim(),
          cau_dia_pago: Number(formData.cau_dia_pago),
          Default: formData.Default,
        };
        await insertCuota(payload);
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Cuota creada exitosamente.",
          confirmButtonColor: "#3085d6",
        });
      }

      setIsModalOpen(false);
      setFormData(emptyForm);
      await fetchCuotas();
    } catch (error) {
      console.error("Error al guardar cuota:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No fue posible guardar la cuota de administración.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-military-600">Cuotas de Administración</h2>
          <Button
            onClick={openCreateModal}
            className="bg-military-500 hover:bg-military-600 text-white rounded-lg transition flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Agregar cuota
          </Button>
        </div>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white">
          <CardHeader>
            <CardTitle className="text-xl text-military-600">Listado de cuotas</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-500" />
              </div>
            ) : cuotas.length === 0 ? (
              <p className="text-gray-600 text-center py-4">No hay cuotas registradas para esta unidad.</p>
            ) : (
              <>
                <div className="md:hidden space-y-4">
                  {cuotas.map((cuota) => (
                    <div key={cuota.cau_codigo} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
                      <div className="space-y-2 text-sm">
                        <p><span className="font-semibold">Nombre:</span> {cuota.cau_nombre || "-"}</p>
                        <p><span className="font-semibold">Valor:</span> ${cuota.cau_valor.toLocaleString()}</p>
                        <p><span className="font-semibold">Día de pago:</span> {cuota.cau_dia_pago}</p>
                        <p><span className="font-semibold">Banco:</span> {cuota.cau_banco}</p>
                        <p><span className="font-semibold">Default:</span> {cuota.cau_default_texto}</p>
                        <p><span className="font-semibold">Activo:</span> {cuota.cau_activo_texto}</p>
                        <p className="break-all">
                          <span className="font-semibold">Link:</span>{" "}
                          {cuota.cau_link_pago ? (
                            <a
                              href={cuota.cau_link_pago}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                              <LinkIcon className="h-3 w-3" />
                              Ver link
                            </a>
                          ) : (
                            "-"
                          )}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(cuota)}
                        className="mt-4 w-full flex items-center justify-center gap-1"
                      >
                        <Pencil className="h-3 w-3" />
                        Editar
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Día de pago</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Default</TableHead>
                        <TableHead>Activo</TableHead>
                        <TableHead>Link</TableHead>
                        <TableHead>Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cuotas.map((cuota) => (
                        <TableRow key={cuota.cau_codigo}>
                          <TableCell>{cuota.cau_nombre || "-"}</TableCell>
                          <TableCell>${cuota.cau_valor.toLocaleString()}</TableCell>
                          <TableCell>{cuota.cau_dia_pago}</TableCell>
                          <TableCell>{cuota.cau_banco}</TableCell>
                          <TableCell>{cuota.cau_default_texto}</TableCell>
                          <TableCell>{cuota.cau_activo_texto}</TableCell>
                          <TableCell>
                            {cuota.cau_link_pago ? (
                              <a
                                href={cuota.cau_link_pago}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                              >
                                <LinkIcon className="h-3 w-3" />
                                Ver link
                              </a>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(cuota)}
                              className="flex items-center gap-1"
                            >
                              <Pencil className="h-3 w-3" />
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-4 sm:p-5 rounded-lg shadow-xl w-full max-w-xl max-h-[95vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4 text-military-600">
                {isEditing ? "Editar cuota de administración" : "Crear cuota de administración"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="Nombre" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <Input
                    id="Nombre"
                    name="Nombre"
                    value={formData.Nombre}
                    onChange={handleInputChange}
                    placeholder="Ej: Cuota ordinaria"
                  />
                </div>
                <div>
                  <label htmlFor="valor" className="block text-sm font-medium text-gray-700 mb-1">
                    Valor cuota *
                  </label>
                  <Input
                    id="valor"
                    name="valor"
                    type="number"
                    value={formData.valor}
                    onChange={handleInputChange}
                    placeholder="Ej: 120000"
                  />
                </div>
                <div>
                  <label htmlFor="cau_dia_pago" className="block text-sm font-medium text-gray-700 mb-1">
                    Día de pago *
                  </label>
                  <Input
                    id="cau_dia_pago"
                    name="cau_dia_pago"
                    type="number"
                    value={formData.cau_dia_pago}
                    onChange={handleInputChange}
                    placeholder="1 - 31"
                  />
                </div>
                <div>
                  <label htmlFor="Banco" className="block text-sm font-medium text-gray-700 mb-1">
                    Banco *
                  </label>
                  <Input
                    id="Banco"
                    name="Banco"
                    value={formData.Banco}
                    onChange={handleInputChange}
                    placeholder="Nombre del banco"
                  />
                </div>
                <div>
                  <label htmlFor="link_Pago" className="block text-sm font-medium text-gray-700 mb-1">
                    Link de pago *
                  </label>
                  <Input
                    id="link_Pago"
                    name="link_Pago"
                    value={formData.link_Pago}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <p className="block text-sm font-medium text-gray-700 mb-2">Default *</p>
                  <div className="flex items-center gap-6">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="Default"
                        checked={formData.Default === 1}
                        onChange={() => setFormData((prev) => ({ ...prev, Default: 1 }))}
                      />
                      Sí
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="Default"
                        checked={formData.Default === 0}
                        onChange={() => setFormData((prev) => ({ ...prev, Default: 0 }))}
                      />
                      No
                    </label>
                  </div>
                </div>
                {isEditing && (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">Activo</p>
                    <div className="flex items-center gap-6">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="Activo"
                          checked={formData.Activo === 1}
                          onChange={() => setFormData((prev) => ({ ...prev, Activo: 1 }))}
                        />
                        Sí
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="Activo"
                          checked={formData.Activo === 0}
                          onChange={() => setFormData((prev) => ({ ...prev, Activo: 0 }))}
                        />
                        No
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:flex-1"
                  disabled={isSaving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  className="w-full sm:flex-1 bg-military-500 hover:bg-military-600 text-white"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : isEditing ? (
                    "Actualizar"
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}